import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavScroll } from "../../hooks/useNavScroll";
import { cn } from "../../lib/utils";
import MaltoraLogo from "./MaltoraLogo";

type NavLink = {
  label: string;
  to: string;
  match?: (path: string, hash: string) => boolean;
  hashTarget?: string;
};

const NAV_LINKS: NavLink[] = [
  { label: "Home", to: "/", match: (p) => p === "/" },
  { label: "Pricing", to: "/pricing", match: (p) => p.startsWith("/pricing") },
  { label: "Blog", to: "/blog", match: (p) => p.startsWith("/blog") },
  {
    label: "About Us",
    to: "/#about",
    match: (p, h) => p === "/" && h === "#about",
    hashTarget: "#about",
  },
  {
    label: "Trust & Security",
    to: "/trust",
    match: (p) => p.startsWith("/trust"),
  },
];

export default function Navbar() {
  const scrolled = useNavScroll();
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    requestAnimationFrame(() => {
      if (hash) {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
      }
      window.scrollTo(0, 0);
    });
  }, [pathname, hash]);
  const close = () => setOpen(false);

  const renderLink = (link: NavLink) => (
    <Link
      key={link.label}
      to={link.to}
      className={cn("nav-link", link.match?.(pathname, hash) && "active")}
      onClick={(e) => {
        if (link.hashTarget && pathname === "/" && hash === link.hashTarget) {
          e.preventDefault();
          const el = document.querySelector(link.hashTarget);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        close();
      }}
    >
      {link.label}
    </Link>
  );

  return (
    <nav className={cn("maltora-nav", scrolled && "scrolled")}>
      <div className="nav-inner">
        <Link className="nav-logo" to="/" onClick={close}>
          <MaltoraLogo />
        </Link>

        <div className="nav-links">{NAV_LINKS.map(renderLink)}</div>

        <div className="nav-actions">
          <Link
            to="/signin"
            state={{ fromApp: true }}
            className="nav-text-link"
          >
            Sign in
          </Link>
          <Link to="/contact" state={{ fromApp: true }} className="nav-cta">
            Get in touch
          </Link>
        </div>

        <button
          className="nav-burger"
          aria-label="Menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div className={cn("nav-mobile", open && "is-open")}>
        {NAV_LINKS.map(renderLink)}
        <Link
          to="/signin"
          state={{ fromApp: true }}
          className="nav-text-link"
          onClick={close}
        >
          Sign in
        </Link>
        <Link
          to="/contact"
          state={{ fromApp: true }}
          className="nav-cta"
          onClick={close}
        >
          Get in touch
        </Link>
      </div>
    </nav>
  );
}
